import type { AuthPrompt } from "./msalcommon";

/**
 * How the fallback sign-in dialog (dialog.html) should sign in.
 *
 * The taskpane opens the dialog through the Office Dialog API, which runs its
 * own standard MSAL instance outside the Outlook host's nested app auth broker.
 * The two sides only share a query string, so the options travel there and
 * both sides read them through this module.
 *
 * Kept free of Office.js and MSAL runtime imports so the rules can be tested.
 */
export interface DialogSignInOptions {
    // Every cached path (silent, popup, or a token the server rejected) has
    // already failed, so the dialog must not answer from its own cache either
    forceFresh?: boolean;
    // The user signed out explicitly and must get the account picker
    selectAccount?: boolean;
}

export interface DialogSignInPlan {
    trySilent: boolean;
    // Undefined lets Entra reuse its session without asking anything
    prompt?: AuthPrompt;
}

// Office reports this dialog event when the user closes the window (the X
// button) before it posted a result
export const DIALOG_CLOSED_EVENT_CODE = 12006;
export const DIALOG_CLOSED_MESSAGE = "Login window closed";

// Office refuses to open the dialog with this code when the host asked the
// user whether the add-in may open a window and the user chose Ignore. The
// web-based hosts (Outlook on the web, new Outlook for Windows) show that
// prompt before every dialog, so the sign-in window never appears and the
// user has nothing to close. Pressing Try again and choosing Allow is all it
// takes, which is worth saying instead of reporting a generic failure.
export const DIALOG_IGNORED_ERROR_CODE = 12009;

// Reads the host error code the taskpane keeps on a failed open (see
// buildDialogOpenError in plugin.tsx)
export const isDialogPromptIgnoredError = (error: any): boolean =>
    error != null && (error as any).officeErrorCode === DIALOG_IGNORED_ERROR_CODE;

export const buildDialogPath = (options: DialogSignInOptions = {}): string => {
    const params: string[] = [];
    if (options.forceFresh) {
        params.push("forceFresh=1");
    }
    if (options.selectAccount) {
        params.push("selectAccount=1");
    }
    return params.length > 0 ? `dialog.html?${params.join("&")}` : "dialog.html";
};

export const parseDialogSignInOptions = (search: string): DialogSignInOptions => {
    const params = new URLSearchParams(search || "");
    return {
        forceFresh: params.get("forceFresh") === "1",
        selectAccount: params.get("selectAccount") === "1",
    };
};

/**
 * Decides the dialog's sign-in steps.
 *
 * forceFresh never asks for credentials. It used to redirect with
 * prompt=login, which made the user type their password (and repeat MFA) every
 * time the Outlook broker served an expired token, roughly weekly on new
 * Outlook for Windows. Users closed that window, and it surfaced as MM-03.
 * Skipping the dialog's own cache is enough: a redirect always returns an ID
 * token Entra has just issued, and Entra can issue it from its existing
 * session without any interaction.
 */
export const planDialogSignIn = (options: DialogSignInOptions = {}): DialogSignInPlan => {
    if (options.forceFresh) {
        return {
            trySilent: false,
            prompt: options.selectAccount ? "select_account" : undefined,
        };
    }
    return { trySilent: true, prompt: "select_account" };
};
